import React, { useCallback, useMemo } from "react";

import {ReactComponent as ArrowDown} from "../../../../assets/images/arrow-down.svg";
import {ReactComponent as ArrowUp} from "../../../../assets/images/arrow-up.svg";
import {
	ICON_FB,
	ICON_HOME,
	ICON_LINK,
	ICON_LINKEDIN,
	ICON_MAIL,
	ICON_PUSH,
	ICON_TWITTER,
	ICON_YOUTUBE,
	ICON_BOOSTED
} from "../../../../constants/icons";
import {formatTimeSinceShort} from "../../../../hooks/formatTimeSince";
import {formatFullNumber} from "../../../../hooks/formatNumber";
import {formatReadingTime} from "../../../../hooks/formatReadingTime";
import {formatTotalReadingTime} from "../../../../hooks/formatTotalReadingTime";
import Line from "./Line";
import {formatDate} from "../../../admin/helper/formatters";
import {IListDataItem} from "../../../../constants/interfaces";
import formatReadDepth from "../../../../hooks/formatReadDepth";
import useGetEnvironment from "../../../../hooks/useGetEnvironment";
import formatCustomMetric from "../../../../hooks/formatCustomMetric";

type Props = {
	data: IListDataItem,
	lineSum: number,
	lineWidth: number,
	numberFields: Array<string>,
	customFields: Array<any>,
	showBrand: boolean,
	showSection: boolean,
	variant: string,
	showNotifications: boolean
}

const ListItem = (props: Props) => {
	// if channel is in story config, show as active
	const placeIcons = (secondaryPosts: Array<{post_type: string}>) => {
		const icons = ['fb_post', 'tweet', 'newsletter', 'homepage_link', 'site_link', 'linkedin', 'pushmessage', 'youtube', 'boosted', 'boosted1'];
		const activeIcons = secondaryPosts?.map(item => item.post_type) ?? [];

		return icons.map((icon, index) => {
			switch (icon) {
				case "fb_post":
					return <ICON_FB active={activeIcons.indexOf(icon) !== -1} key={index}/>;
				case "tweet":
					return <ICON_TWITTER active={activeIcons.indexOf(icon) !== -1} key={index}/>;
				case "newsletter":
					return <ICON_MAIL active={activeIcons.indexOf(icon) !== -1} key={index}/>;
				case "homepage_link":
					return <ICON_HOME active={activeIcons.indexOf(icon) !== -1} key={index}/>;
				case "site_link":
					return <ICON_LINK active={activeIcons.indexOf(icon) !== -1} key={index}/>;
				case "linkedin":
					return <ICON_LINKEDIN active={activeIcons.indexOf(icon) !== -1} key={index}/>
				case "pushmessage":
					return <ICON_PUSH active={activeIcons.indexOf(icon) !== -1} key={index}/>;
				case "youtube":
					return <ICON_YOUTUBE active={activeIcons.indexOf(icon) !== -1} key={index}/>;
				case "boosted":
					return <ICON_BOOSTED active={activeIcons.indexOf(icon) !== -1} key={index}/>
				default:
					return <React.Fragment key={index}></React.Fragment>;
			}
		});
	}
	const showUserNeeds = false;//User needs are hidden for now.
	const { data, lineSum, lineWidth, numberFields, customFields, showBrand, showSection, variant } = props;
	const { pubdate, title, author, brandName, section, trend, notifications, secondaryPosts, uri } = data;
	const getEnvironment = useGetEnvironment();
	const add = trend >= 0;
	const createUrl = useMemo(()=>{
		let url = "";
		const environment = getEnvironment();
		if (environment === "staging" || environment === "staging2") {
			url = `https://${data.brandId}-${environment}.smartocto.com/#/${data.type && data.type === 'landing' ? 'pages' : 'stories'}/${data.relationId}/`
		} else {
			url = `https://${data.brandId}.smartocto.com/#/${data.type && data.type === 'landing' ? 'pages' : 'stories'}/${data.relationId}/`
		}
		return url
	},[data.brandId, data.relationId, data.type, getEnvironment])

	const formatSection = useCallback(
		(sections: string) => {
			if (!sections) {
				return '';
			}
			const capitalize = (input: string) => {
				const words = input.replaceAll('-', ' ').split(' ');
				const uppercaseWords = words.map((word) => word.charAt(0).toUpperCase() + word.slice(1));
				return `${uppercaseWords.join(' ')}`;
			};

			const sectionsArray = sections.split(",");
			const moreThanThree = sectionsArray.length > 3;
			const sectionsShorten = [] as any;
			
			sectionsArray.forEach((section,index) => {
				if (index >= 3) return;
				const subSections = section.split(">");
				let shortenSectionName = "";
				if (subSections.length > 2) {
					shortenSectionName = "..." + (subSections[subSections.length - 2] + " > " + subSections[subSections.length - 1])
					sectionsShorten.push(shortenSectionName);
				} else {
					sectionsShorten.push(section)
				}
			})
			
			if (Array.isArray(sectionsShorten) && sectionsShorten.length > 1) {
				return <span>
					{
						sectionsShorten.map((section, index)=>{
							if (moreThanThree && index === 2) {
								return <>
									<span className="single-section" key={section}>{capitalize(section)},</span>
									<span className="single-section">...</span>
								</>
							} 
							else if (index !== sectionsShorten.length - 1) {
								return <span className="single-section" key={section}>{capitalize(section)},</span>					
							}
							return <span className="single-section" key={section}>{capitalize(section)}</span>
						})
					}
				</span>
			}

			return capitalize(sections);
		},[section, data])
	

	return (
		<div className="list-item">
			<div className="list-item__row list-item__row--social-icons">
				<p className="list-item__timeframe">
					{formatTimeSinceShort(pubdate)}
				</p>
				{placeIcons(secondaryPosts)}
				{showUserNeeds && data.user_needs && data.user_needs.emotion_driven === true && <span className="user-needs-flag">EMO</span> }
				{showUserNeeds && data.user_needs && data.user_needs.context_driven === true && <span className="user-needs-flag">CON</span> }
				{showUserNeeds && data.user_needs && data.user_needs.fact_driven === true && <span className="user-needs-flag">FAC</span> }
				{showUserNeeds && data.user_needs && data.user_needs.action_driven === true && <span className="user-needs-flag">ACT</span> }
				{ variant === 'full' && author ? (
					<p className="list-item__author">{author}</p>
				  ) : null }
			</div>
			<div className="list-item__row">
				<div className="list-item__header">
					{add ? (
						<ArrowUp className="list-item__arrow" />
					) : (
						<ArrowDown className="list-item__arrow" />
					)}
					<h3 className="list-item__title ">
						{createUrl ? (
							<a href={createUrl} target="_blank" rel="noreferrer">
								{title ? title : uri}
							</a>
						) : (
							title
						)}
					</h3>
				</div>
				<div className={`list-item-stats dynamic-flex-${
					numberFields.length + Number(variant === 'full' && showSection) + (isNaN(Number(brandName))? 0:1)
					}`}>
				{numberFields.map((field, idx) => {
					if (data[field] || data[field] === 0) {
						if (field === "pubdate") {
							return <span className="list-item-stat date" key={idx}>{formatDate(data[field])}</span>;
						}
						if (field === "averageReadingTime") {
							return <span className="list-item-stat readtime" key={idx}>{formatReadingTime(data[field])}</span>;
						}
						if (field === "totalReadingTime") {
							return <span className="list-item-stat readtime" key={idx}>{formatTotalReadingTime(data[field])}</span>;
						}
						if (field === "averageReadDepth" || field === 'averageScrollDepth') {
							return <span className="list-item-stat readtime" key={idx}>{formatReadDepth(data[field])}</span>;
						}
						if (customFields.some(customField => customField.id === field)) {
							const customField = customFields.find(customField => customField.id === field);
							return <span className="list-item-stat" key={idx}>{formatCustomMetric(data[field], customField.format)}</span>;	
						}
						
						return <span className="list-item-stat" key={idx}>{formatFullNumber(data[field])}</span>;
					} else {
						return <span className="list-item-stat" key={idx}>n/a</span>
					}
				})}
				{showSection && (
					<div className="list-item-stat list-item__extra">
						{showSection && section && (
							<p className="list-item__section">
								{formatSection(section)}
							</p>
						)}
						{props.showNotifications && (
							<p className="list-item__actions">
								<span><span>{notifications}</span></span>
							</p>
						)}
					</div>
				)}
				{showBrand && brandName && (
					<div className="list-item-stat list-item__extra">
						<div className="list-item__brand">
							<span>{brandName}</span>
						</div>
					</div>
				)}
				</div>
			</div>
			{
				data.isPaid && (
					<div className="list-item__paid"></div>
				)
			}
			<Line data={data} width={lineWidth} sum={lineSum}/>
		</div>
	);
};

export default ListItem;
